import React, { useEffect, useState, useMemo } from "react"
import styled from "styled-components"
import { useMediaQuery } from "react-responsive"
import Column from "src/components/column/column.component"
import SkeletonLoading from "src/components/skeleton-loading"
import { BlogCard } from "src/components/blogs-components"
import Row from "src/components/row"
import Colors from "src/theme/colors"
import Footer from "src/components/footer"
import ApolloClient from "apollo-client"
import { NormalizedCacheObject } from "apollo-cache-inmemory"
import { fetchBlogList, fetchTags } from "./utils/services"
import Spinner from "src/components/spinner"
import AuthenticationState from "src/hoc/authentication/authentication.state"
import TabBar, { ListType } from "src/components/tab-bar"
import { TabContainer } from "../single-blog/UI"
import { useLocation } from "react-router"
import Loader from "src/dashboards/components/loader"
import EmptyLeases from "src/containers/leases/empty-leases"
import { Select } from "antd"
import { Helmet } from "react-helmet-async"
import "./style.scss"
import { UserType } from "src/generated/graphql"
import { checkAllAccess } from "src/utils/comman-methods"
import { updateTag } from "src/services"

export const SkeletonWrapper = styled(Column as any)`
  align-items: center;
  justify-content: center;
  flex-direction: row;
  display: flex;
  width: 100%;
  padding: 20px;
  flex-wrap: wrap;
  @media (max-width: 900px) {
    padding: 10px;
    padding-top: 30px;
  }
`

const BlogRootContainer = styled.div`
  margin: 5px auto;
`
const MoreBlock = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  margin: 20px 0px;
  cursor: pointer;
`

const ButtonComponent = styled.div`
  display: flex;
  font-size: 17px;
  padding: 5px 20px;
  border-radius: 6px;
  text-transform: uppercase;
  color: ${Colors.primary};
  border: 1px solid ${Colors.primary};
`
const LeaseContainer = styled.div`
  display: flex;
  height: 200px;
  text-align: center;
`

export type PermissionAccessType = {
  edit: boolean
  add: boolean
  delete: boolean
  create: boolean
}
interface Props {
  client: ApolloClient<NormalizedCacheObject>
  authState: AuthenticationState
}

export const renderSkeleton = () => <SkeletonLoading rows={4} imgHeight={"160px"} isImage />

var tagArray = [
  {
    name: "All",
    key: "All"
  }
]

const options = [
  {
    label: "New",
    value: "new"
  },
  {
    label: "Popular",
    value: "popular"
  }
]

const Blogs = (props: Props) => {
  const { client, authState } = props
  const location = useLocation()

  const locationTag = useMemo(
    () =>
      location.search
        ?.split("=")[1]
        ?.trim()
        .replace(/%20/g, " ")
        .replace(/[\s_-]+/g, " ")
        .replace(/^-+|-+$/g, ""),
    []
  )
  const setTagsField = tag => {
    for (let i = 0; i < tag.length; i++) {
      const tagObj = {
        name: tag[i].name,
        key: tag[i].name,
        id: tag[i].id
      }
      tagArray.push(tagObj)
    }
    return tagArray
  }

  const handleChange = val => {
    setSortBy(val)
    sortBlogList(val)
  }

  const [sortBy, setSortBy] = useState<string>()
  const [loading, setLoading] = useState<boolean>(true)
  const [loader, setLoader] = useState<boolean>(false)
  const [isLoadMore, setIsLoadMore] = useState<boolean>(false)
  const [count, setCount] = useState<number>(0)
  const [data, setData] = useState<Array<{}>>([])
  const [optionsList, setOptionsList] = useState<ListType[]>([])
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 768px)" })

  const isPermissionToDelete = useMemo(() => {
    const { state } = authState
    if (state?.user && state?.user.userType === UserType.SubAdmin) {
      let allAccess: PermissionAccessType = checkAllAccess(state?.user?.permissions, "blogs")
      if (allAccess?.edit) return true
      else return false
    }
    if (state?.user?.userType == UserType.Admin || state?.user?.userType == UserType.SubAdmin) {
      return true
    } else return false
  }, [authState])

  const handleTabChange = (selectedTab: string) => {
    if (selectedTab === "All") filterData()
    else filterData(selectedTab)
  }

  const filterData = async (selectedTab = "") => {
    setLoader(true)
    const resp = await fetchBlogList(client, 0, authState, selectedTab, true, "")
    if (resp && resp.data) {
      const { blogs } = resp.data
      setData(blogs || [])
    }
    setLoader(false)

    if (selectedTab) {
      const tabsList = optionsList.length > 0 ? optionsList : tagArray
      const tag: any = tabsList.find(item => item?.name === selectedTab)
      updateTag(client, tag?.id)
    }
    setLoading(false)
  }

  const sortBlogList = async sortBy => {
    setLoader(true)
    const resp = await fetchBlogList(client, 0, authState, "", true, sortBy)
    if (resp && resp.data) {
      const { blogs } = resp.data
      setData(blogs || [])
    }
    setLoader(false)
  }

  const initTags = async () => {
    const resp = await fetchTags(client)
    if (resp && resp.data) {
      const { getTags } = resp.data
      setTagsField(getTags)
    }
  }
  const init = async (skipNo: number = 0) => {
    setLoading(true)
    const resp = await fetchBlogList(client, skipNo, authState, "", true, "")
    if (resp && resp.data) {
      const { blogs, blogCount } = resp.data
      setData(prev => [...prev, ...blogs] || [])
      setCount((blogCount && blogCount.count) || 0)
    }
    setLoading(false)
    setIsLoadMore(false)
  }

  const loadMore = () => {
    setIsLoadMore(true)
    init(data && data.length)
  }

  const updateOptions = (list: ListType[]) => {
    setOptionsList(list)
  }

  useEffect(() => {
    initTags()
    if (locationTag) filterData(locationTag)
    else init()
  }, [])

  return (
    <>
      <Helmet>
        <title>Tolobi Blog - Explore Insights & Tips on Renting in Canada</title>
        <meta
          name="description"
          content="Get the latest updates, along with expert insights and valuable tips with the Tolobi Blogs. Stay ahead of the game with our informative articles on renting in Canada."
        />
        <link rel="canonical" href={window?.location?.href} />
      </Helmet>
      <>
        {loading && !isLoadMore ? (
          <SkeletonWrapper>
            {[...Array(isTabletOrMobile ? 1 : 3)].map(renderSkeleton)}
          </SkeletonWrapper>
        ) : (
          <>
            <BlogRootContainer>
              <div className="dropdown-container">
                <Select
                  style={{ width: 120, borderRadius: 6 }}
                  defaultValue="new"
                  value={sortBy}
                  onChange={handleChange}
                  options={options}
                />
              </div>
              <TabContainer style={{ marginBottom: "30px" }}>
                <TabBar
                  tabsList={optionsList.length > 0 ? optionsList : tagArray}
                  handleTabChange={handleTabChange}
                  defaultKey={location.search?.split("=")[1]}
                  isCrossActive={isPermissionToDelete}
                  client={client}
                  updateOptions={updateOptions}
                >
                  <></>
                </TabBar>
              </TabContainer>
              <>
                <Row flexWrap="wrap" justifyContent="center">
                  {data?.length > 0 ? (
                    data.map((list: any, index: number) => (
                      <BlogCard list={list} key={list.id} authState={authState} client={client} />
                    ))
                  ) : (
                    <LeaseContainer>
                      <EmptyLeases title="You currently don't have any blogs" />
                    </LeaseContainer>
                  )}
                </Row>
                {data && data.length < count && !isLoadMore ? (
                  <MoreBlock>
                    <ButtonComponent onClick={loadMore}>View More Articles</ButtonComponent>
                  </MoreBlock>
                ) : null}
                {isLoadMore && <Spinner height={100} />}
              </>
            </BlogRootContainer>
          </>
        )}
      </>
      <Loader loader={loader} />
      <Footer />
    </>
  )
}

export default Blogs
